import { imageFromEnv } from "@p0security/integrations-gcloud-shared/mcp/servers";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { ServiceNowAiIconUrl } from "./icon.ts";
import { serviceNowAiMcpTools } from "./mcp-tools.ts";
import type { ServiceNowAiIntegration } from "./types.ts";

// The gating step is the IAM binding on the vaulted secret, not ServiceNow
// itself: the token is written at grant time, but the session cannot read it
// until the binding propagates.
const serviceNowPropagationHint = propagationHint({
  resource: "ServiceNow",
  normalWait: "15 to 45 seconds",
  maxWait: "2 minutes",
  pollInterval: "10 seconds",
});

const prefixArgs = ["servicenow-ai", "role"];

/** The P0-built `servicenow` image over the instance's Table API.
 *
 * A container rather than ServiceNow's own MCP server, which needs a Now
 * Assist SKU and exposes only the tools an admin publishes. The token is for
 * the ServiceNow user P0 made for the gateway session, so ServiceNow's roles
 * and ACLs decide what each tool can reach.
 */
export const serviceNowAiMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: "ServiceNow",
  iconUrl: ServiceNowAiIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its session's secret name; read that rather than
    // rebuilding the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  authorization: "upstream",
  transport: "stdio",
  tools: { type: "manual", tools: serviceNowAiMcpTools },
  container: {
    command: [],
    args: [],
    envs: {
      SERVICENOW_TOKEN: "{{credential.token}}",
      SERVICENOW_INSTANCE: "{{request.permission.instanceId}}",
    },
    image: {
      image: imageFromEnv({
        env: "SERVICENOW_IMAGE",
        fallback:
          "p0security/servicenow:f93adcd@sha256:2e8e5015bf63f4426033761ce980ef29ca457f73a3b771f11843e79098044ece",
      }),
    },
  },
  access: {
    prompt: `Request ServiceNow access by calling with argv:

  [...<roles>, "--instance", <instance>, "--reason", <reason>]

where:

  - roles: Name(s) of the ServiceNow roles your tool calls need (e.g. itil,
    approver_user); use the list tool to see the ones you can request
  - instance: The instance name in <name>.service-now.com; you can leave it
    out when only one instance is installed
  - reason: The reason access is needed

You act as your own ServiceNow user, which has only the roles you are granted.
ServiceNow returns no records, not an error, for a table your roles may not
read.
`,
    prefixArgs,
    propagationHint: serviceNowPropagationHint,
  },
  list: {
    fields: {
      instance: {
        description:
          "ServiceNow instance, by the name in <name>.service-now.com",
        required: [],
      },
      role: {
        description: "ServiceNow role, by name",
        required: [],
      },
    },
    prefixArgs,
  },
  location: {
    key: "instance",
    description: "ServiceNow instance, by the name in <name>.service-now.com",
  },
};

/** Whether {@link serviceNowAiMcp} can be added: only once an instance is
 * installed, since only that install makes agent users. */
export const serviceNowAiMcpServerAvailability = {
  servicenow: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "instance",
      (installed["servicenow-ai"] ?? {}) as ServiceNowAiIntegration
    ).length > 0,
};
